import { Button, Form, InputNumber, message, PopConfirm, Table } from '@maxtropy/components';
import {
  apiV2MicrogridTopologyPvEmissionRateDetailPost,
  apiV2MicrogridTopologyPvEmissionRateSavePost,
} from '@maxtropy/dmes-apis-v2';
import { useRequest } from 'ahooks';
import { Space } from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { PermissionsType } from '@/common/permissionsConst';
import { useHasPermission } from '@/utils/hooks';
import EditPvEmissionRateModal from './EditPvEmissionRateModal';
import { PvBaselineTargetFormValues, PvEmissionRateRecord } from './types';

export interface PvParamsTabRef {
  save: () => Promise<void>;
  isEditing: boolean;
  loading: boolean;
}

const PERMANENT_END_DATE = dayjs('9999-12-31');

const formatDate = (date?: string) => {
  if (!date) {
    return '';
  }

  const dateValue = dayjs(date);
  return dateValue.isValid() ? dateValue.format('YYYY-MM-DD') : date;
};

const formatEndDate = (endDate?: string) => {
  if (!endDate) {
    return '-';
  }

  const dateValue = dayjs(endDate);
  if (!dateValue.isValid()) {
    return endDate;
  }

  return dateValue.isSame(PERMANENT_END_DATE, 'day') ? '-' : dateValue.format('YYYY-MM-DD');
};

const normalizeEmissionRateList = (list: PvEmissionRateRecord[]) =>
  [...list]
    .sort((prev, next) => dayjs(prev.startDate).valueOf() - dayjs(next.startDate).valueOf())
    .map((item, index, sortedList) => {
      const nextStartDate = sortedList[index + 1]?.startDate;
      return {
        ...item,
        endDate: nextStartDate ? dayjs(nextStartDate).subtract(1, 'day').format('YYYY-MM-DD') : undefined,
      };
    });

const PvParamsTab = forwardRef<PvParamsTabRef>((_, ref) => {
  const hasSetupPermission = useHasPermission(PermissionsType.B_PV_SIDE_PARAMETER_CONFIGURATION);
  const [form] = Form.useForm<PvBaselineTargetFormValues>();
  const [isEditing, setIsEditing] = useState(false);
  const [dataSource, setDataSource] = useState<PvEmissionRateRecord[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<PvEmissionRateRecord>();
  const { refreshAsync: refreshPvEmissionRateDetail, loading: detailLoading } = useRequest(() =>
    apiV2MicrogridTopologyPvEmissionRateDetailPost({}).then(res => {
      setDataSource(
        normalizeEmissionRateList(
          (res.pvEmissionRateList ?? []).map((item, index) => ({
            id: item.id ?? Date.now() + index,
            startDate: item.startDate ?? '',
            endDate: item.endDate,
            rate: item.rate ?? 0,
          }))
        )
      );
      form.setFieldsValue({ pvBaselineTarget: res.pvBaselineTarget });
    })
  );
  const { runAsync: savePvEmissionRate, loading: saving } = useRequest(apiV2MicrogridTopologyPvEmissionRateSavePost, {
    manual: true,
  });

  const columns: ColumnsType<PvEmissionRateRecord> = useMemo(
    () => [
      {
        title: '时间',
        dataIndex: 'startDate',
        render: (_, record) => `${formatDate(record.startDate)} 至 ${formatEndDate(record.endDate)}`,
      },
      {
        title: '价格(元/kgCO2e)',
        dataIndex: 'rate',
        render: value => Number(value).toFixed(2),
      },
      {
        title: '操作',
        width: 180,
        render: (_, record) => (
          <Space size={12}>
            <Button type="link" htmlType="button" disabled={!isEditing} onClick={() => setEditingRecord(record)}>
              编辑
            </Button>
            <PopConfirm title="确定删除该光伏减排费率区间？" onConfirm={() => handleDelete(record.id)}>
              <Button type="link" htmlType="button" danger disabled={!isEditing}>
                删除
              </Button>
            </PopConfirm>
          </Space>
        ),
      },
    ],
    [isEditing]
  );

  const handleDelete = (id: number) => {
    setDataSource(list => normalizeEmissionRateList(list.filter(item => item.id !== id)));
  };

  const handleModalOk = (values: PvEmissionRateRecord) => {
    const hasCurrent = dataSource.some(item => item.id === values.id);

    setDataSource(list => {
      if (hasCurrent) {
        return normalizeEmissionRateList(list.map(item => (item.id === values.id ? values : item)));
      }
      return normalizeEmissionRateList([...list, values]);
    });
    setModalOpen(false);
    setEditingRecord(undefined);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingRecord(undefined);
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    await savePvEmissionRate({
      pvEmissionRateList: dataSource.map(item => ({
        startDate: item.startDate,
        rate: item.rate,
      })),
      pvBaselineTarget: values.pvBaselineTarget,
    });
    await refreshPvEmissionRateDetail();
    message.success('保存成功');
    setIsEditing(false);
  };

  const handleCancel = async () => {
    await refreshPvEmissionRateDetail();
    setIsEditing(false);
  };

  useImperativeHandle(ref, () => ({
    save: handleSave,
    isEditing,
    loading: detailLoading || saving,
  }));

  return (
    <>
      <Button
        type="primary"
        htmlType="button"
        wrapStyle={{ marginBottom: 10 }}
        disabled={!isEditing}
        onClick={() => setModalOpen(true)}
      >
        新建光伏减排单价
      </Button>
      <Table rowKey="id" loading={detailLoading} pagination={false} columns={columns} dataSource={dataSource} />
      <Form form={form} layout="horizontal" disabled={!isEditing} style={{ maxWidth: 520, marginTop: 16 }}>
        <Form.Item
          label="光伏侧基线目标"
          name="pvBaselineTarget"
          rules={[{ type: 'number', min: 0, max: 99999999.99, message: '请输入0~99999999.99' }]}
        >
          <InputNumber
            placeholder="请输入"
            addonAfter="MW"
            min={0}
            max={99999999.99}
            precision={2}
            style={{ width: '100%' }}
          />
        </Form.Item>
      </Form>
      <Space size={8} className="sticky-footer" style={{ zIndex: 99, paddingLeft: 0, marginTop: 28 }}>
        {isEditing ? (
          <>
            <Button type="primary" htmlType="button" loading={saving} onClick={handleSave}>
              保存
            </Button>
            <Button htmlType="button" onClick={handleCancel}>
              取消
            </Button>
          </>
        ) : (
          hasSetupPermission && (
            <Button type="primary" htmlType="button" onClick={() => setIsEditing(true)}>
              编辑
            </Button>
          )
        )}
      </Space>
      <EditPvEmissionRateModal
        open={modalOpen || !!editingRecord}
        record={editingRecord}
        records={dataSource}
        onCancel={closeModal}
        onOk={handleModalOk}
      />
    </>
  );
});

export default PvParamsTab;
